import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import ZoomMtgEmbedded, { EmbeddedClient, SuspensionViewType, SuspensionViewValue } from '@zoom/meetingsdk/embedded';
import { ZoomComponentConfig, ZoomRole, ZoomViewType } from '../../_models/models';

type SignatureResponse = { signature: string };

type Props = {
    zoom?: ZoomComponentConfig;
    show: boolean;
    observeParent?: boolean; // default true
    className?: string;
};

export default function ZoomComponentView({ zoom, show, observeParent = true, className }: Props) {
    const w = 260;
    const h = 400;

    const wrapperRef = useRef<HTMLDivElement | null>(null);

    // Create an embedded client exactly once (synchronously). This survives parent re-renders.
    const clientRef = useRef<any>(null);
    if (!clientRef.current) {
        clientRef.current = ZoomMtgEmbedded.createClient();
    }

    // Use state so React re-renders when the root element appears.
    const [zoomRootEl, setZoomRootEl] = useState<HTMLDivElement | null>(null);
    const zoomRootRef = useCallback((el: HTMLDivElement | null) => {
        setZoomRootEl(el);
    }, []);

    const [joined, setJoined] = useState(false);
    const [joining, setJoining] = useState(false);
    const [size, setSize] = useState<{ width: number; height: number }>({ width: 640, height: 322 });

    const viewType: SuspensionViewValue = 'active';

    // Track which meeting we actually joined to avoid re-joining on harmless re-renders / new zoom object refs.
    const joinedMeetingRef = useRef<string | null>(null);
    const joinInFlightRef = useRef(false);
    const initDoneRef = useRef(false);
    const joiningMeetingRef = useRef<string | null>(null);

    // // Observe container size (wrapper should fill the TableSlot)
    // useLayoutEffect(() => {
    //     if (!observeParent) return;
    //
    //     // OBSERVE THE PARENT SLOT (innerTableSlot), not ZoomDock itself
    //     const slotEl = wrapperRef.current?.parentElement as HTMLElement | null;
    //     if (!slotEl) return;
    //
    //     const ro = new ResizeObserver(entries => {
    //         const cr = entries[0]?.contentRect;
    //         if (!cr) return;
    //
    //         const w = Math.max(240, Math.floor(cr.width));
    //         const h = Math.max(180, Math.floor(cr.height));
    //
    //         setSize(prev => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
    //     });
    //
    //     ro.observe(slotEl);
    //     return () => ro.disconnect();
    // }, [observeParent]);

    // Update Zoom video options when size changes (safe no-op if not supported)
    // useEffect(() => {
    //     try {
    //         clientRef.current?.updateVideoOptions?.({
    //             defaultViewType: viewType,
    //             viewSizes: {
    //                 iewSizes: { default: { width: w, height: h } }
    //             }
    //         });
    //     } catch {
    //         // ignore
    //     }
    // }, [size.width, size.height]);

    const meetingNumber = zoom?.meetingNumber ? String(zoom.meetingNumber) : '';
    const canRun = useMemo(() => {
        return Boolean(
            show && zoom?.sdkKey && meetingNumber && zoom?.userName && zoom?.signatureEndpoint && zoomRootEl && clientRef.current
        );
    }, [show, zoom?.sdkKey, zoom?.userName, zoom?.signatureEndpoint, meetingNumber, zoomRootEl]);

    // Join/leave lifecycle:
    // - join when canRun becomes true and we haven't joined this meetingNumber
    // - leave when unmounted OR meeting changes OR show becomes false
    useEffect(() => {
        if (!canRun) {
            // Leave if we were joined or joining
            if (joinedMeetingRef.current || joiningMeetingRef.current) {
                try {
                    clientRef.current?.leaveMeeting?.();
                } catch {}
            }
            joinedMeetingRef.current = null;
            joiningMeetingRef.current = null;
            joinInFlightRef.current = false;
            initDoneRef.current = false;
            setJoined(false);
            setJoining(false);
            return;
        }

        // Already joined OR already joining this meeting => do nothing
        if (joinedMeetingRef.current === meetingNumber) return;
        if (joiningMeetingRef.current === meetingNumber) return;
        if (joinInFlightRef.current) return;

        let cancelled = false;

        const run = async () => {
            joinInFlightRef.current = true;
            joiningMeetingRef.current = meetingNumber;

            setJoining(true);
            setJoined(false);

            try {
                // init only once per mount/session (re-init while joining can also cause trouble)
                if (!initDoneRef.current) {
                    await clientRef.current.init({
                        zoomAppRoot: zoomRootEl,
                        language: zoom!.language ?? 'en-US',
                        patchJsMedia: true,

                        videoOptions: {
                            defaultViewType: viewType,
                            isResizable: false,
                            viewSizes: {
                                default: { width: w, height: h }
                            }
                        }
                    });
                    initDoneRef.current = true;
                }

                if (cancelled) return;

                await clientRef.current.join({
                    signature: zoom!.signatureEndpoint, // <-- signature already provided
                    sdkKey: zoom!.sdkKey,
                    meetingNumber: zoom!.meetingNumber,
                    password: zoom!.password ?? '',
                    userName: zoom!.userName,
                    userEmail: zoom!.userEmail ?? ''
                });

                if (cancelled) return;

                joinedMeetingRef.current = meetingNumber;
                setJoined(true);
            } catch (e) {
                console.error('Zoom Component View error:', e);
                joinedMeetingRef.current = null;
                initDoneRef.current = false;
                if (!cancelled) setJoined(false);
            } finally {
                joinInFlightRef.current = false;
                joiningMeetingRef.current = null;
                if (!cancelled) setJoining(false);
            }
        };

        run();

        return () => {
            cancelled = true;
        };
        // IMPORTANT: do NOT depend on size here, or it can restart joins during resizes.
    }, [canRun, meetingNumber, zoom?.sdkKey, zoom?.signatureEndpoint, zoom?.userName, zoomRootEl]);

    // Leave on unmount (true cleanup)
    useEffect(() => {
        return () => {
            try {
                clientRef.current?.leaveMeeting?.();
            } catch {}
            joinedMeetingRef.current = null;
        };
    }, []);

    return (
        <div ref={wrapperRef} className={['ZoomDock', className].filter(Boolean).join(' ')}>
            <div ref={zoomRootRef} className="ZoomDockRoot" style={{ width: '100%', height: '100%' }} />

            {!joined && (
                <div
                    className="ZoomDockOverlay"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'grid',
                        placeItems: 'center',
                        background: 'rgba(0,0,0,0.35)',
                        color: '#fff',
                        pointerEvents: 'none'
                    }}
                >
                    {joining ? 'Connecting…' : `${zoom!.meetingNumber}`}
                </div>
            )}
        </div>
    );
}
